import { ReactSession } from "react-client-session";
import React, { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  increment,
} from "firebase/firestore";
import { createReviewObject, QUESTIONS } from "./create-review-object";
import { debounce } from "lodash";

export const ReviewContext = React.createContext({});

export const ReviewProvider = ({ children }) => {
  const debugQuestions = false;

  const [loading, setLoading] = useState(false);
  const [allReviews, setAllReviews] = useState([]);
  const [submittedReview, setSubmittedReview] = useState(null);

  const addReview = async (inputs, onComplete) => {
    setLoading(true);

    if (debugQuestions) {
      for (let i = 0; i < QUESTIONS.length; i++) {
        const newReview = createReviewObject(inputs, i);
        const docRef = await addDoc(collection(db, "reviews"), newReview);
        console.log("Document written with ID: ", docRef.id);

        setAllReviews((prevAllReview) => [
          ...prevAllReview,
          { id: docRef.id, ...newReview },
        ]);
      }
      onComplete?.();
    }

    if (!debugQuestions) {
      try {
        const newReview = createReviewObject(inputs);

        const docRef = await addDoc(collection(db, "reviews"), newReview);

        if (docRef?.id) {
          ReactSession.set("submitted_review_id", docRef.id);
          console.log("Document written with ID: ", docRef.id);

          // Set review client-side
          setSubmittedReview(newReview);
          setAllReviews((prevAllReview) => [
            ...prevAllReview,
            { id: docRef.id, ...newReview },
          ]);
          onComplete?.();
        }
      } catch (e) {
        console.error("Error adding document: ", e);
      }
    }

    setLoading(false);
  };

  const refreshReviews = async () => {
    console.log("refreshing reviews");
    try {
      const dbReviews = await getDocs(collection(db, "reviews"));
      const formattedAllReviews = [];
      dbReviews.forEach((doc) => {
        formattedAllReviews.push({ id: doc.id, ...doc.data() });
      });

      setAllReviews(formattedAllReviews);
    } catch (e) {
      console.error("error fetching reviews", e);
    }

    setLoading(false);
  };

  const onRefreshReviews = async (force) => {
    setLoading(true);

    if (force) {
      await refreshReviews();
      return;
    }

    debounceRefreshReviews.current();
  };

  const debounceRefreshReviews = useRef(debounce(refreshReviews, 2000));

  useEffect(() => {
    const submittedReviewId = ReactSession.get("submitted_review_id");

    const myReview = allReviews.find(({ id }) => id === submittedReviewId);

    if (myReview) {
      setSubmittedReview(myReview);
    }
  }, [allReviews]);

  const likeReview = async (id, likes) => {
    try {
      const reviewRef = doc(db, "reviews", id);

      await updateDoc(reviewRef, {
        likes: increment(1),
      });

      setAllReviews((allReviews) =>
        allReviews.map((r) =>
          id === r.id ? { ...r, likes: likes + 1 || 1 } : r
        )
      );

      const likedReviewsIds = ReactSession.get("liked_review_ids") || [];
      ReactSession.set("liked_review_ids", [...likedReviewsIds, id]);
    } catch (e) {
      console.error("error updating review likes", e);
    }
  };

  const hasLikedReview = (id) => {
    const likedReviewsIds = ReactSession.get("liked_review_ids") || [];
    return likedReviewsIds.includes(id);
  };

  const clearSession = () => {
    ReactSession.set("submitted_review_id", null);
    // ReactSession.set("liked_review_ids", null);
    setSubmittedReview(null);
  };

  return (
    <ReviewContext.Provider
      value={{
        allReviews,
        submittedReview,
        addReview,
        refreshReviews: onRefreshReviews,
        loading,
        likeReview,
        hasLikedReview,
        clearSession,
        debugQuestions,
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
};
